import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';

interface Producto {
    id: number;
    producto: string;
    descripcion: string;
    cantidad: number;
    observaciones?: string;
}

interface ResponsiveMaintenanceTableProps {
    products: Producto[];
    appState: {
        mode: 'view' | 'new' | 'edit';
    };
    onEditProduct: (product: Producto, index: number) => void;
    onDeleteProduct: (id: number) => void;
}

const ResponsiveMaintenanceTable: React.FC<ResponsiveMaintenanceTableProps> = ({
    products,
    appState,
    onEditProduct,
    onDeleteProduct
}) => {
    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es móvil - AQUÍ puedes cambiar el breakpoint
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1100); 
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Componentes de renderizado para tabla de escritorio
    const actionBodyTemplate = (rowData: Producto) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <Button
                icon="pi pi-pencil"
                size="small"
                severity="warning"
                rounded
                onClick={() => {
                    const index = products.findIndex(p => p.id === rowData.id);
                    onEditProduct(rowData, index);
                }}
                disabled={appState.mode === 'view'}
                tooltip="Editar producto"
            />
            <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                rounded
                onClick={() => onDeleteProduct(rowData.id)}
                disabled={appState.mode === 'view'}
                tooltip="Eliminar producto"
            />
        </div>
    );

    const cantidadBodyTemplate = (rowData: Producto) => (
        <div style={{ textAlign: 'center' }}>
            <Badge value={rowData.cantidad} severity="info" />
        </div>
    );

    const observacionesBodyTemplate = (rowData: Producto) => (
        <span style={{ 
            fontSize: '0.85rem',
            color: rowData.observaciones ? '#374151' : '#9ca3af',
            fontStyle: rowData.observaciones ? 'normal' : 'italic'
        }}>
            {rowData.observaciones || 'Sin observaciones'}
        </span>
    );

    // Renderizado de tarjetas móviles
    const renderMobileCards = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {products.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '40px 20px',
                    color: '#6b7280', fontSize: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    🔧 No hay productos agregados
                    <br />
                    <span style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                        Utilice el formulario superior para agregar productos
                    </span>
                </div>
            ) : (
                products.map((product, index) => (
                    <div
                        key={product.id}
                        style={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '16px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            position: 'relative'
                        }}
                    >
                        {/* Header de la tarjeta */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #f3f4f6'
                        }}>
                            <div>
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontWeight: '500',
                                    marginBottom: '2px'
                                }}>
                                    ID: {product.id}
                                </div>
                                <div style={{
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#1f2937'
                                }}>
                                    🔑 {product.producto}
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <Badge 
                                    value={product.cantidad} 
                                    severity="info"
                                    style={{ fontSize: '0.8rem' }}
                                />
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <Button
                                        icon="pi pi-pencil"
                                        size="small"
                                        severity="warning"
                                        rounded
                                        onClick={() => onEditProduct(product, index)}
                                        disabled={appState.mode === 'view'}
                                        tooltip="Editar"
                                        style={{ width: '28px', height: '28px' }}
                                    />
                                    <Button
                                        icon="pi pi-trash"
                                        size="small"
                                        severity="danger"
                                        rounded
                                        onClick={() => onDeleteProduct(product.id)}
                                        disabled={appState.mode === 'view'}
                                        tooltip="Eliminar"
                                        style={{ width: '28px', height: '28px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contenido de la tarjeta */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                                <span style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontWeight: '500'
                                }}>
                                    📝 Descripción:
                                </span>
                                <div style={{
                                    fontSize: '0.9rem',
                                    color: '#374151',
                                    marginTop: '2px'
                                }}>
                                    {product.descripcion}
                                </div>
                            </div>

                            {product.observaciones && (
                                <div style={{ marginTop: '8px' }}>
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        fontWeight: '500'
                                    }}>
                                        💬 Observaciones:
                                    </span>
                                    <div style={{
                                        fontSize: '0.85rem',
                                        color: '#374151',
                                        marginTop: '2px',
                                        padding: '8px',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        {product.observaciones}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    // Renderizado de tabla de escritorio
    const renderDesktopTable = () => (
        <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
        }}>
            <DataTable
                value={products}
                responsiveLayout="scroll"
                stripedRows
                size="small"
                emptyMessage={
                    <div style={{
                        textAlign: 'center', padding: '40px 20px',
                        color: '#6b7280', fontSize: '1rem'
                    }}>
                        🔧 No hay productos agregados
                        <br />
                        <span style={{ fontSize: '0.85rem', marginTop: '8px', display: 'block' }}>
                            Utilice el formulario superior para agregar productos
                        </span>
                    </div>
                }
                className="p-datatable-sm"
                style={{ fontSize: '0.9rem' }}
                scrollable
                scrollHeight="400px"
                rowHover
            >
                <Column 
                    field="id" 
                    header="🆔 ID" 
                    headerStyle={{ 
                        width: '80px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ textAlign: 'center', padding: '8px', fontWeight: '600' }}
                />
                <Column 
                    field="producto" 
                    header="🔑 Producto" 
                    headerStyle={{ 
                        width: '150px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px', fontWeight: '600', color: '#374151' }}
                />
                <Column 
                    field="descripcion" 
                    header="📝 Descripción" 
                    headerStyle={{ 
                        minWidth: '250px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px', color: '#374151' }}
                />
                <Column 
                    field="cantidad" 
                    header="🔢 Cantidad" 
                    headerStyle={{ 
                        width: '100px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    body={cantidadBodyTemplate}
                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                />
                <Column 
                    field="observaciones" 
                    header="💬 Observaciones" 
                    headerStyle={{ 
                        minWidth: '200px', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ padding: '8px' }}
                    body={observacionesBodyTemplate}
                />
                <Column 
                    body={actionBodyTemplate} 
                    header="⚙️ Acciones" 
                    headerStyle={{ 
                        width: '120px', textAlign: 'center', fontWeight: 'bold',
                        backgroundColor: '#f8fafc', color: '#374151'
                    }} 
                    bodyStyle={{ textAlign: 'center', padding: '8px' }}
                />
            </DataTable>
        </div>
    );

    return (
        <>
            {/* CSS para responsive */}
            <style>{`
                @media screen and (max-width: 1100px) {
                    .p-datatable {
                        display: none !important;
                    }
                }
                
                @media screen and (min-width: 1100px) {
                    .mobile-cards {
                        display: none !important;
                    }
                }
                
                @media screen and (max-width: 1100px) {
                    .mobile-card {
                        margin: 0 -8px;
                        border-radius: 8px !important;
                    }
                    
                    .mobile-card-content {
                        padding: 12px !important;
                    }
                    
                    .mobile-card-header {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    
                    .mobile-card-actions {
                        align-self: flex-end !important;
                    }
                }
            `}</style>

            {/* Renderizado condicional */}
            <div className="desktop-table">
                {renderDesktopTable()}
            </div>
            
            <div className="mobile-cards">
                {renderMobileCards()}
            </div>

            {/* Resumen de productos */}
            {products.length > 0 && (
                <div style={{
                    marginTop: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px'
                }}>
                    <span style={{ 
                        color: '#0369a1', 
                        fontWeight: '600',
                        fontSize: isMobile ? '0.85rem' : '1rem'
                    }}>
                        🔧 Total de productos: {products.length}
                    </span>
                    <span style={{ 
                        color: '#0369a1', 
                        fontWeight: '600',
                        fontSize: isMobile ? '0.85rem' : '1rem'
                    }}>
                        🔢 Cantidad total: {products.reduce((sum, p) => sum + p.cantidad, 0)}
                    </span>
                </div>
            )}
        </>
    );
};

export default ResponsiveMaintenanceTable;